/**
 * Its So Clean Website API Service
 *
 * Serviço centralizado para comunicação com a API do website Its So Clean.
 * Facilita adicionar novas funcionalidades e endpoints.
 *
 * API Base URL: https://bora-isc-backend.n2skox.easypanel.host
 */

const API_BASE_URL = process.env.REACT_APP_ISC_API_URL || 'https://bora-isc-backend.n2skox.easypanel.host';

// Credenciais do admin via variáveis de ambiente
const ADMIN_CREDENTIALS = {
  email: process.env.REACT_APP_ISC_ADMIN_EMAIL,
  password: process.env.REACT_APP_ISC_ADMIN_PASSWORD
};

// Token storage key
const TOKEN_KEY = 'isc_api_token';
const TOKEN_EXPIRY_KEY = 'isc_api_token_expiry';

/**
 * Obtém o token armazenado
 */
const getStoredToken = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

  if (!token || !expiry) return null;

  // Verificar se expirou (com margem de 1 hora)
  const expiryDate = new Date(expiry);
  const now = new Date();
  const marginMs = 60 * 60 * 1000; // 1 hora

  if (now.getTime() > expiryDate.getTime() - marginMs) {
    // Token expirado ou próximo de expirar
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    return null;
  }

  return token;
};

/**
 * Armazena o token
 */
const storeToken = (token) => {
  // JWT válido por 7 dias
  const expiry = new Date();
  expiry.setDate(expiry.getDate() + 7);

  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toISOString());
};

/**
 * Faz login na API e retorna o token
 */
const login = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(ADMIN_CREDENTIALS),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Falha no login');
    }

    const data = await response.json();

    if (data.success && data.token) {
      storeToken(data.token);
      return data.token;
    }

    throw new Error('Token não recebido');
  } catch (error) {
    console.error('Erro no login Its So Clean API:', error);
    throw error;
  }
};

/**
 * Obtém token válido (do storage ou faz login)
 */
const getValidToken = async () => {
  let token = getStoredToken();

  if (!token) {
    token = await login();
  }

  return token;
};

/**
 * Faz requisição autenticada à API
 */
const authenticatedRequest = async (endpoint, options = {}) => {
  const token = await getValidToken();

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  // Se token inválido, tentar login novamente
  if (response.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);

    const newToken = await login();

    return fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${newToken}`,
        ...options.headers,
      },
    });
  }

  return response;
};

// ============================================
// ENDPOINTS DA API
// ============================================

/**
 * Obtém estatísticas do dashboard
 * @returns {Promise<{totalSubmissions, newSubmissions, repliedSubmissions, totalUsers}>}
 */
export const getStats = async () => {
  const response = await authenticatedRequest('/api/stats');

  if (!response.ok) {
    throw new Error('Falha ao obter estatísticas');
  }

  return response.json();
};

/**
 * Lista submissões com filtros
 * @param {Object} params - Parâmetros de filtro
 * @param {string} params.status - Filtrar por status (new, read, replied, archived)
 * @param {string} params.search - Buscar por nome, email ou telefone
 * @param {number} params.limit - Limite de resultados (default: 50)
 * @param {number} params.offset - Offset para paginação (default: 0)
 * @returns {Promise<{submissions: Array, pagination: Object}>}
 */
export const getSubmissions = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.status) queryParams.append('status', params.status);
  if (params.search) queryParams.append('search', params.search);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);

  const queryString = queryParams.toString();
  const endpoint = `/api/submissions${queryString ? `?${queryString}` : ''}`;

  const response = await authenticatedRequest(endpoint);

  if (!response.ok) {
    throw new Error('Falha ao obter submissões');
  }

  return response.json();
};

/**
 * Obtém detalhes de uma submissão específica
 * @param {string} id - ID da submissão
 * @returns {Promise<{submission: Object, replies: Array}>}
 */
export const getSubmissionById = async (id) => {
  const response = await authenticatedRequest(`/api/submissions/${id}`);

  if (!response.ok) {
    throw new Error('Falha ao obter submissão');
  }

  return response.json();
};

/**
 * Atualiza o status de uma submissão
 * @param {string} id - ID da submissão
 * @param {string} status - Novo status (new, read, replied, archived)
 * @returns {Promise<Object>}
 */
export const updateSubmissionStatus = async (id, status) => {
  const response = await authenticatedRequest(`/api/submissions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });

  if (!response.ok) {
    throw new Error('Falha ao atualizar status');
  }

  return response.json();
};

/**
 * Envia resposta para uma submissão (por email)
 * @param {string} id - ID da submissão
 * @param {string} message - Mensagem de resposta
 * @returns {Promise<Object>}
 */
export const replyToSubmission = async (id, message) => {
  const response = await authenticatedRequest(`/api/submissions/${id}/reply`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });

  if (!response.ok) {
    throw new Error('Falha ao enviar resposta');
  }

  return response.json();
};

/**
 * Arquiva uma submissão
 * @param {string} id - ID da submissão
 * @returns {Promise<Object>}
 */
export const archiveSubmission = async (id) => {
  return updateSubmissionStatus(id, 'archived');
};

/**
 * Marca submissão como não lida
 * @param {string} id - ID da submissão
 * @returns {Promise<Object>}
 */
export const markAsUnread = async (id) => {
  return updateSubmissionStatus(id, 'new');
};

// ============================================
// UTILITÁRIOS
// ============================================

/**
 * Mapeia serviço para nome legível
 */
export const SERVICE_NAMES = {
  'regular-cleaning': 'Limpeza Regular',
  'deep-cleaning': 'Limpeza Profunda',
  'move-out-cleaning': 'Limpeza de Mudança',
  'carpet-cleaning': 'Limpeza de Carpete',
  'commercial-cleaning': 'Limpeza Comercial',
  'airbnb-cleaning': 'Limpeza Airbnb',
  'window-cleaning': 'Limpeza de Vidros',
  'other': 'Outro',
};

/**
 * Mapeia status para nome legível e cor
 */
export const STATUS_CONFIG = {
  'new': { label: 'Novo', color: 'primary', icon: '🆕' },
  'read': { label: 'Lido', color: 'info', icon: '👁️' },
  'replied': { label: 'Respondido', color: 'success', icon: '✅' },
  'archived': { label: 'Arquivado', color: 'secondary', icon: '📦' },
};

/**
 * Formata data para exibição
 */
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Export default com todos os métodos
const itsSoCleanApi = {
  getStats,
  getSubmissions,
  getSubmissionById,
  updateSubmissionStatus,
  replyToSubmission,
  archiveSubmission,
  markAsUnread,
  SERVICE_NAMES,
  STATUS_CONFIG,
  formatDate,
};

export default itsSoCleanApi;
